import apiClient from './client'
import type {
  Workflow,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowExecution,
  ApiResponse,
  PaginatedResponse,
} from '@/types/models'
import {
  adaptListResponse,
  adaptWorkflowResponse,
  adaptWorkflowRequest,
} from '@/lib/utils/api-adapter'

/**
 * Workflow API endpoints
 */
export const workflowsApi = {
  /**
   * List all workflows for the current user
   */
  async list(params?: {
    limit?: number
    offset?: number
    enabled?: boolean
  }): Promise<PaginatedResponse<Workflow>> {
    const response = await apiClient.get<any>('/api/v1/workflows', {
      params,
    })
    const adapted = adaptListResponse<any>(response, 'workflows')
    return {
      ...adapted,
      data: adapted.data.map(adaptWorkflowResponse),
    }
  },

  /**
   * Get a single workflow by ID
   */
  async get(id: string): Promise<ApiResponse<Workflow>> {
    const response = await apiClient.get<any>(`/api/v1/workflows/${id}`)
    return {
      data: adaptWorkflowResponse(response),
    }
  },

  /**
   * Create a new workflow
   */
  async create(data: CreateWorkflowRequest): Promise<ApiResponse<Workflow>> {
    const apiRequest = adaptWorkflowRequest(data)
    const response = await apiClient.post<any>('/api/v1/workflows', apiRequest)
    return {
      data: adaptWorkflowResponse(response),
    }
  },

  /**
   * Update an existing workflow
   */
  async update(
    id: string,
    data: UpdateWorkflowRequest
  ): Promise<ApiResponse<Workflow>> {
    const apiRequest = adaptWorkflowRequest(data)
    const response = await apiClient.put<any>(
      `/api/v1/workflows/${id}`,
      apiRequest
    )
    return {
      data: adaptWorkflowResponse(response),
    }
  },

  /**
   * Delete a workflow
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/v1/workflows/${id}`)
  },

  /**
   * Enable a workflow
   */
  async enable(id: string): Promise<ApiResponse<Workflow>> {
    const response = await apiClient.post<any>(
      `/api/v1/workflows/${id}/enable`
    )
    return {
      data: adaptWorkflowResponse(response),
    }
  },

  /**
   * Disable a workflow
   */
  async disable(id: string): Promise<ApiResponse<Workflow>> {
    const response = await apiClient.post<any>(
      `/api/v1/workflows/${id}/disable`
    )
    return {
      data: adaptWorkflowResponse(response),
    }
  },

  /**
   * Get workflow execution history
   */
  async getExecutions(
    id: string,
    params?: { limit?: number; offset?: number }
  ): Promise<PaginatedResponse<WorkflowExecution>> {
    return apiClient.get<PaginatedResponse<WorkflowExecution>>(
      `/api/v1/workflows/${id}/executions`,
      { params }
    )
  },

  /**
   * Test a workflow with sample data
   */
  async test(
    id: string,
    testData?: Record<string, any>
  ): Promise<ApiResponse<WorkflowExecution>> {
    return apiClient.post<ApiResponse<WorkflowExecution>>(
      `/api/v1/workflows/${id}/test`,
      testData
    )
  },
}

export default workflowsApi
